import { useState } from "react";
import { Mail, ArrowLeft, CheckCircle, Lock } from "lucide-react";
import { recoverPassword } from "../../lib/api/auth";

interface FormErrors {
  email?: string;
}

interface ForgotPasswordPageProps {
  onBackClick?: () => void;
  onLoginClick?: () => void;
}

export default function ForgotPasswordPage({
  onLoginClick,
}: ForgotPasswordPageProps) {
  const [email, setEmail] = useState<string>("");
  const [errors, setErrors] = useState<FormErrors>({});
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!email) {
      newErrors.email = "Email é obrigatório";
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = "Email inválido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (validateForm()) {
      setIsLoading(true);
      try {
        await recoverPassword(email);
        setEmailSent(true);
      } catch (error) {
        console.error("Erro ao recuperar senha:", error);
        setErrors({ email: "Ocorreu um erro ao enviar o email de recuperação. Tente novamente." });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleResend = async () => {
    setIsLoading(true);
    try {
      await recoverPassword(email);
      alert("Email reenviado com sucesso!");
    } catch (error) {
      console.error("Erro ao reenviar email:", error);
      alert("Erro ao reenviar email. Tente novamente.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    if (emailSent) {
      setEmailSent(false);
    } else if (onLoginClick) {
      onLoginClick();
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-(--bg-secondary) flex items-center justify-center p-4">
        <button
          onClick={handleBackClick}
          className="fixed top-6 left-6 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-(--text-tertiary) transition-all duration-200 hover:text-(--text-primary) hover:bg-(--bg-accent) z-10"
        >
          <ArrowLeft className="w-5 h-5" />
          <span>Voltar</span>
        </button>

        <div className="w-full max-w-md lg:max-w-5xl">
          <div
            className="bg-(--card-bg) rounded-2xl shadow-lg border lg:flex lg:overflow-hidden"
            style={{ borderColor: "var(--border-color)" }}
          >
            {/* Logo Section - Desktop */}
            <div
              className="hidden lg:flex lg:w-5/12 lg:flex-col lg:items-center lg:justify-center lg:p-12"
              style={{ backgroundColor: "var(--bg-secondary)" }}
            >
              <div
                className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6"
                style={{ backgroundColor: "rgba(24, 128, 56, 0.1)" }}
              >
                <CheckCircle
                  className="w-20 h-20"
                  style={{ color: "var(--success-color)" }}
                />
              </div>
              <h2
                className="text-2xl font-bold mb-2"
                style={{ color: "var(--text-primary)" }}
              >
                Email Enviado
              </h2>
              <p
                className="text-center text-base"
                style={{ color: "var(--text-tertiary)" }}
              >
                Verifique sua caixa de entrada
              </p>
            </div>

            {/* Content Section */}
            <div className="p-8 lg:w-7/12 lg:p-12 text-center lg:text-left">
              {/* Success Icon - Mobile */}
              <div
                className="lg:hidden inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                style={{ backgroundColor: "rgba(24, 128, 56, 0.1)" }}
              >
                <CheckCircle
                  className="w-12 h-12"
                  style={{ color: "var(--success-color)" }}
                />
              </div>

              <h1
                className="text-3xl font-bold mb-4"
                style={{ color: "var(--text-primary)" }}
              >
                Email Enviado!
              </h1>

              <p
                className="text-base mb-5"
                style={{ color: "var(--text-tertiary)" }}
              >
                Enviamos um link de recuperação para:
              </p>

              <div
                className="p-4 rounded-lg mb-5"
                style={{ backgroundColor: "var(--bg-secondary)" }}
              >
                <p
                  className="font-medium"
                  style={{ color: "var(--primary-color)" }}
                >
                  {email}
                </p>
              </div>

              <p
                className="text-sm mb-6"
                style={{ color: "var(--text-tertiary)" }}
              >
                Verifique sua caixa de entrada e siga as instruções para
                redefinir sua senha. O link expira em 1 hora.
              </p>

              <div className="space-y-3">
                <button
                  onClick={onLoginClick}
                  className="w-full text-white py-3 px-4 rounded-lg font-semibold hover:shadow-lg transition-all shadow-md"
                  style={{ backgroundColor: "var(--primary-color)" }}
                >
                  Voltar ao Login
                </button>

                <button
                  onClick={handleResend}
                  disabled={isLoading}
                  className="w-full py-3 px-4 rounded-lg font-medium border hover:bg-(--bg-accent) transition-all disabled:opacity-50"
                  style={{
                    borderColor: "var(--border-color)",
                    color: "var(--text-tertiary)",
                    backgroundColor: "var(--card-bg)",
                  }}
                >
                  {isLoading ? "Enviando..." : "Reenviar Email"}
                </button>
              </div>

              <div
                className="mt-6 p-4 rounded-lg"
                style={{ backgroundColor: "rgba(249, 171, 0, 0.1)" }}
              >
                <p
                  className="text-sm"
                  style={{ color: "var(--text-tertiary)" }}
                >
                  💡 <strong>Dica:</strong> Verifique também sua pasta de spam
                  ou lixo eletrônico.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-(--bg-secondary) flex items-center justify-center p-4">
      <button
        onClick={handleBackClick}
        className="fixed top-6 left-6 flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-(--text-tertiary) transition-all duration-200 hover:text-(--text-primary) hover:bg-(--bg-accent) z-10"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Voltar</span>
      </button>

      <div className="w-full max-w-md lg:max-w-5xl">
        <div
          className="bg-(--card-bg) rounded-2xl shadow-lg border lg:flex lg:overflow-hidden"
          style={{ borderColor: "var(--border-color)" }}
        >
          {/* Logo Section - Desktop */}
          <div
            className="hidden lg:flex lg:w-5/12 lg:flex-col lg:items-center lg:justify-center lg:p-12"
            style={{ backgroundColor: "var(--bg-secondary)" }}
          >
            <div
              className="inline-flex items-center justify-center w-24 h-24 rounded-full mb-6"
              style={{ backgroundColor: "var(--primary-color)" }}
            >
              <div className="w-20 h-20 rounded-lg flex items-center justify-center">
                <Lock className="w-14 h-14 text-white" />
              </div>
            </div>
            <h2
              className="text-2xl font-bold mb-2"
              style={{ color: "var(--text-primary)" }}
            >
              Recupere sua senha
            </h2>
            <p
              className="text-center text-base"
              style={{ color: "var(--text-tertiary)" }}
            >
              Digite seu email para continuar
            </p>
          </div>

          {/* Form Section */}
          <div className="p-8 lg:w-7/12 lg:p-12">
            {/* Header - Mobile */}
            <div className="text-center mb-8 lg:text-left">
              <div
                className="lg:hidden inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                style={{ backgroundColor: "rgba(25, 103, 210, 0.1)" }}
              >
                <div
                  className="w-12 h-12 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: "var(--primary-color)" }}
                >
                  <Lock className="w-7 h-7 text-white" />
                </div>
              </div>
              <h1
                className="text-3xl font-bold mb-2"
                style={{ color: "var(--text-primary)" }}
              >
                Esqueceu sua senha?
              </h1>
              <p
                className="text-base"
                style={{ color: "var(--text-tertiary)" }}
              >
                Sem problemas! Digite seu email e enviaremos instruções para
                redefinir sua senha.
              </p>
            </div>

            {/* Form */}
            <div className="space-y-5">
              {/* Email Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium mb-2"
                  style={{ color: "var(--text-primary)" }}
                >
                  Email
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail
                      className="h-5 w-5"
                      style={{ color: "var(--text-tertiary)" }}
                    />
                  </div>
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full pl-10 pr-3 py-3 border rounded-lg focus:outline-none focus:ring-2 transition disabled:opacity-50 placeholder-(--text-tertiary)"
                    style={{
                      borderColor: errors.email
                        ? "var(--danger-color)"
                        : "var(--border-color)",
                      color: "var(--text-primary)",
                      backgroundColor: "var(--input-bg)",
                    }}
                    placeholder="seu@email.com"
                    disabled={isLoading}
                  />
                </div>
                {errors.email && (
                  <p
                    className="mt-1 text-sm"
                    style={{ color: "var(--danger-color)" }}
                  >
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Submit Button */}
              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className="w-full text-white py-3 px-4 rounded-lg font-semibold hover:shadow-lg focus:outline-none focus:ring-2 transition-all shadow-md disabled:opacity-50"
                style={{ backgroundColor: "var(--primary-color)" }}
              >
                {isLoading ? "Enviando..." : "Enviar Link de Recuperação"}
              </button>
            </div>

            {/* Info Box */}
            <div
              className="mt-6 p-4 rounded-lg border"
              style={{
                borderColor: "var(--border-color)",
                backgroundColor: "var(--bg-secondary)",
              }}
            >
              <p className="text-sm" style={{ color: "var(--text-tertiary)" }}>
                <strong>Não recebeu o email?</strong> Verifique se o endereço
                está correto e confira sua pasta de spam. Caso precise de ajuda,
                entre em contato com o suporte.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
